import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enCommon from '../locales/en/common.json';

/* ===========================================================================
 * ARABIC IS OFF, AND OFF IS A ONE-LINE CHANGE BACK.
 *
 * The owner asked for the product to run in English only for now. What that
 * has to mean, to be worth calling off: the toggle is gone from the shell AND
 * nothing else can select `ar` behind the reader's back — a browser sending
 * `Accept-Language: ar`, or a `lang: "ar"` still sitting in the localStorage of
 * anyone who used the toggle before today. Both of those come in through the
 * detector, and `supportedLngs` is the one gate they all pass; an unsupported
 * detection falls to `fallbackLng`, so this line alone turns both cases into
 * English. That is also why the catalogue is no longer registered as a
 * resource: a locale nothing may resolve to is a megabyte of dead weight in
 * the bundle.
 *
 * NOTHING IS DELETED. `src/locales/ar/common.json` stays on disk, complete and
 * still maintained by the same key discipline — putting `'ar'` back in this
 * array and re-adding its resource is the whole of turning it back on, and
 * `isRtlLocale`/`applyDocumentLang` below stay for exactly that day.
 * ======================================================================== */
export const SUPPORTED_LOCALES = ['en'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** Locales that should flip the layout to right-to-left. */
const RTL_LOCALES: ReadonlySet<string> = new Set(['ar', 'he', 'fa', 'ur']);

export function isRtlLocale(lng: string | undefined): boolean {
  if (!lng) return false;
  // Match "ar", "ar-EG", etc. by checking the language subtag only.
  return RTL_LOCALES.has(lng.split('-')[0]!);
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    nonExplicitSupportedLngs: true,
    debug: import.meta.env.DEV,
    defaultNS: 'common',
    ns: ['common'],
    resources: {
      en: { common: enCommon },
    },
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'lang',
    },
    react: {
      useSuspense: false,
    },
  });

/**
 * Apply `dir`/`lang` attributes to `<html>` based on the active locale.
 * Wired to an i18next `languageChanged` listener in `main.tsx`.
 */
export function applyDocumentLang(lng: string): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = lng;
  document.documentElement.dir = isRtlLocale(lng) ? 'rtl' : 'ltr';
}

export default i18n;
